import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  getProductTitle,
  getProductDescription,
  getProductKeywords,
  getProductFeatures
} from '../../utils/dataFallbacks';

export function ProductSchema({ product }) {
  if (!product) return null;

  const {
    brand,
    image_url,
    price,
    asin,
    stock_status,
    rating,
    review_count,
    category_slug,
    category,
    updated_at
  } = product;

  const baseUrl = 'https://www.techieday.com';

  const title = getProductTitle(product);
  const description = getProductDescription(product);
  const keywords = getProductKeywords(product);
  const features = getProductFeatures(product);

  /* =========================
     PRODUCT SCHEMA (SAFE)
  ========================= */
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${baseUrl}/dp/${asin}`,
    "name": title,
    "image": image_url ? [image_url] : undefined,
    "description":
      description ||
      features?.slice(0, 3).join('. ') ||
      title,
    "brand": brand
      ? {
          "@type": "Brand",
          "name": brand
        }
      : undefined,
    "offers": {
      "@type": "Offer",
      "url": `${baseUrl}/dp/${asin}`,
      "priceCurrency": "USD",
      "price": price,
      "availability": stock_status
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Amazon"
      }
    },
    ...(rating > 0 && review_count > 0
      ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": Number(rating.toFixed(1)),
            "reviewCount": review_count,
            "bestRating": 5,
            "worstRating": 1
          }
        }
      : {})
  };

  /* =========================
     BREADCRUMB SCHEMA (FIXED)
  ========================= */
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      ...(category_slug
        ? [
            {
              "@type": "ListItem",
              "position": 2,
              "name": category || category_slug,
              "item": `${baseUrl}/category/${category_slug}`
            }
          ]
        : []),
      ...(brand
        ? [
            {
              "@type": "ListItem",
              "position": category_slug ? 3 : 2,
              "name": brand,
              "item": `${baseUrl}/search/${encodeURIComponent(brand)}`
            }
          ]
        : []),
      {
        "@type": "ListItem",
        "position": category_slug && brand ? 4 : 3,
        "name": title,
        "item": `${baseUrl}/dp/${asin}`
      }
    ]
  };

  return (
    <Helmet>
      {/* =========================
          BASIC META
      ========================= */}
      <title>{title} | TechieDay</title>
      <meta
        name="description"
        content={
          description ||
          `${title}. Track prices, compare deals, and save money. Current price: $${price}.`
        }
      />
      <meta name="keywords" content={keywords.join(', ')} />

      {/* =========================
          FRESHNESS SIGNALS
      ========================= */}
      {updated_at && (
        <>
          <meta
            property="article:modified_time"
            content={new Date(updated_at).toISOString()}
          />
          <meta
            name="last-modified"
            content={new Date(updated_at).toISOString()}
          />
        </>
      )}

      {/* =========================
          OPEN GRAPH
      ========================= */}
      <meta property="og:type" content="product" />
      <meta property="og:title" content={title} />
      <meta
        property="og:description"
        content={`Track ${title} prices and get alerts when prices drop.`}
      />
      <meta property="og:url" content={`${baseUrl}/dp/${asin}`} />
      {image_url && <meta property="og:image" content={image_url} />}
      <meta property="og:site_name" content="TechieDay" />

      {/* =========================
          TWITTER
      ========================= */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta
        name="twitter:description"
        content={`Current price: $${price}. Track price history on TechieDay.`}
      />
      {image_url && <meta name="twitter:image" content={image_url} />}

      {/* =========================
          STRUCTURED DATA
      ========================= */}
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}
